/**
 * Skillsnap TypeScript Types
 * Requirements: Data Models from design.md
 */

// Job Phases
export type JobPhase =
  | 'Queued' | 'Generating' | 'Ready'
  | 'Applied' | 'Follow-Up' | 'Interviewing' | 'Declined' | 'Accepted'
  | 'Skipped' | 'Expired' | 'Errored' | 'Trash';

export const VALID_PHASES: JobPhase[] = [
  'Queued', 'Generating', 'Ready',
  'Applied', 'Follow-Up', 'Interviewing', 'Declined', 'Accepted',
  'Skipped', 'Expired', 'Errored', 'Trash'
];

export const ACTIVE_PHASES: JobPhase[] = [
  'Queued', 'Generating', 'Ready',
  'Applied', 'Follow-Up', 'Interviewing'
];

// Generation States
export type GenerationState = 'locked' | 'ready' | 'generating' | 'complete' | 'error' | 'retry';

// Generation Types
export type GenerationType = 'manual' | 'embedding' | 'generate' | 'omit';

// Subcomponent Types
export type SubcomponentType =
  | 'contact' | 'summary' | 'skills' | 'highlights'
  | 'experience' | 'education' | 'awards' | 'presentations'
  | 'publications' | 'portfolios' | 'coverletter' | 'elevatorpitch';

export const SUBCOMPONENTS: SubcomponentType[] = [
  'contact', 'summary', 'highlights', 'skills',
  'experience', 'education', 'awards', 'presentations',
  'publications', 'portfolios', 'coverletter', 'elevatorpitch'
];

// Default section order for resume rendering (excludes coverletter which is separate)
export const DEFAULT_SECTION_ORDER: SubcomponentType[] = [
  'contact', 'summary', 'highlights', 'skills',
  'experience', 'education', 'awards', 'presentations',
  'publications', 'portfolios'
];

// Generation type restrictions per component
// Components not listed here allow all types: manual, embedding, generate, omit
export const GENERATION_TYPE_RESTRICTIONS: Partial<Record<SubcomponentType, GenerationType[]>> = {
  contact: ['manual', 'omit'],
  summary: ['generate', 'manual', 'omit'],
  skills: ['embedding', 'manual', 'omit'],
  highlights: ['generate', 'embedding', 'omit'],
  experience: ['generate', 'embedding', 'manual', 'omit'],
  education: ['embedding', 'manual', 'omit'],
  awards: ['embedding', 'manual', 'omit'],
  presentations: ['embedding', 'manual', 'omit'],
  publications: ['embedding', 'manual', 'omit'],
  portfolios: ['embedding', 'manual', 'omit'],
  coverletter: ['generate', 'omit'],
  elevatorpitch: ['generate', 'omit'],
};

// Contact Item (for list of contact methods)
export interface ContactItem {
  icon: 'email-at' | 'phone' | 'phone-volume' | 'linkedin' | 'x-twitter' | 'bluesky' | 'github' | 'github-square' | 'globe' | 'house' | 'facebook' | 'instagram' | 'tiktok' | 'youtube' | 'discord' | 'slack' | 'telegram' | 'whatsapp' | 'signal-chat' | 'skillsnap';
  title: string;
  url: string;
  include?: string;
}

// Skill object (was bare string)
export interface Skill {
  title: string;
  tags: string[];
  include?: string;
  // Links the skill to a SkillGroup.id. null/undefined = ungrouped (auto-bucketed on save).
  group_id?: string | null;
}

// Skill Group — a user-curated cluster of skills with a stable PK. At generation
// time, groups are selected per-job and labeled with the closest job theme.
export interface SkillGroup {
  id: string;
  title: string;
  tags: string[];
  order?: number;
}

// Experience Bullet
export interface ExperienceBullet {
  text: string;
  tags: string[];
  include?: string;
  // Set during generation (0.0-1.0). Present on generated/scored copies and
  // round-tripped by the editor; see utils/resumeSchema.
  match_score?: number;
}

// Experience Role
export interface ExperienceRole {
  title: string;
  startDate: string;
  endDate?: string;
  // Canonical parsed date, DERIVED server-side from the raw field on every save
  // (shared/dates.py). Round-trips through the editor untouched — never edited,
  // never displayed; the raw string above is the display source everywhere.
  startDate_iso?: string;
  endDate_iso?: string;
  current: boolean;
  location?: string;
  bullets: ExperienceBullet[];
  include?: string;
}

// Experience Company
export interface ExperienceCompany {
  company: string;
  url: string[];
  employees?: string;
  startDate: string;
  endDate?: string;
  // Canonical parsed date, DERIVED server-side from the raw field on every save
  // (shared/dates.py). Round-trips through the editor untouched — never edited,
  // never displayed; the raw string above is the display source everywhere.
  startDate_iso?: string;
  endDate_iso?: string;
  current: boolean;
  location?: string;
  description?: string;
  roles: ExperienceRole[];
  include?: string;
}

// Education Entry
export interface Education {
  title: string;
  org: string;
  url?: string;
  year: string;
  // Canonical parsed year, DERIVED server-side from `year` on every save.
  year_iso?: string;
  tags: string[];
  include?: string;
}

// Award Entry
export interface Award {
  title: string;
  issuer: string;
  reward: string;
  url?: string;
  year: string;
  // Canonical parsed year, DERIVED server-side from `year` on every save.
  year_iso?: string;
  tags: string[];
  include?: string;
}

// Presentation Entry (formerly Keynote)
export interface Presentation {
  title: string;
  event: string;
  url?: string;
  year: string;
  // Canonical parsed year, DERIVED server-side from `year` on every save.
  year_iso?: string;
  tags: string[];
  include?: string;
}

// Publication Entry
export interface Publication {
  title: string;
  url?: string;
  year: string;
  // Canonical parsed year, DERIVED server-side from `year` on every save.
  year_iso?: string;
  tags: string[];
  include?: string;
}

// Portfolio Entry
export interface Portfolio {
  title: string;
  url?: string;
  year: string;
  // Canonical parsed year, DERIVED server-side from `year` on every save.
  year_iso?: string;
  tags: string[];
  include?: string;
}

// Summary Phrase (for consistent summary generation)
export interface SummaryPhrase {
  text: string;
  tags: string[];
  include?: string;
}

// Summary v2 — structured summary building blocks (features/summary_v2.md).
// Three subsections (identity / credibility / relevance), each 0–N entries;
// generation deterministically selects one (credibility: up to two) per slot
// and a cheap LLM only copy-edits the join. Entries are a tagged-item family
// member with a LOWER per-item tag minimum than the rest (3, not 5).
export interface SummarySectionEntry {
  // Stable PK ('ss_xxxxxxxx'), assigned client-side on add (mirrors syn_/sg_);
  // optional because the server assigns one for entries created elsewhere.
  id?: string;
  text: string;
  tags: string[];
  include?: string;
}

export type SummarySectionKey = 'identity' | 'credibility' | 'relevance';

// ABSENT on a resume that has not adopted Summary v2 — never fabricated on
// load. The key appears the moment the user adds their first entry.
export type SummarySections = Partial<Record<SummarySectionKey, SummarySectionEntry[]>>;

// Proposal shape shared by BOTH Summary v2 CTAs — "Split into sections"
// (decomposes the existing summary) and "Auto-generate Summary Fragments"
// (drafts from the whole resume). Entries carry TEXT ONLY: nothing has been
// saved, so the editor mints the client-side `ss_` ids and the tag pipeline
// fills the tags on the user's next Save.
// `credibility_needs_number` is true when no proposed credibility entry carries
// a digit — the nudge that keeps the slot from silently filling with a claim.
export interface SummarySplitProposal {
  proposed: Record<SummarySectionKey, { text: string }[]>;
  credibility_needs_number: boolean;
}

// Synonym Set — words/phrases the user declares interchangeable in the
// context of this resume (e.g. "Solution Architect" // "Solution Engineer").
// `id` is the stable PK used to reference the set deterministically.
export interface SynonymSet {
  id: string;
  terms: string[];
}

// Implied Fact — something the resume IMPLIES but never states ("15 years in
// data engineering"), derived by the async extraction that runs when the Edit
// Resume lightbox CLOSES. A fact starts 'proposed' and becomes ground truth for
// generation only once the user CONFIRMS it. Facts the user edits or types
// themselves are `source: 'user'` and are never modified by the AI.
export interface ImpliedFact {
  id: string;                        // stable PK, 'if_xxxxxxxx'
  fact: string;                      // short declarative statement (<= 120 chars)
  // 'dismissed' is a TOMBSTONE: the user trashed an AI fact, so it stays in the
  // list (hidden everywhere in the UI) purely so the next extraction run's
  // topic-key compare suppresses it instead of re-asking. See the backend's
  // shared/implied_facts.py for the matching rules.
  status: 'proposed' | 'confirmed' | 'dismissed';
  source: 'ai' | 'user';
  created_at: string;
}

// Lifecycle marker for the implied-facts extraction. Lives on the resume
// RECORD (sibling of `resume_tags`), never inside resumejson — so writing facts
// never perturbs the content hash the trigger keys on.
export interface ImpliedFactsState {
  run_id?: string;
  status?: 'extracting' | 'done';
  started_at?: string;
  finished_at?: string;
  resume_hash?: string;
}

// Demographics — optional EEO/voluntary-disclosure fields. These NEVER appear on
// a generated resume; they exist only to pre-fill application forms via the
// SkillSnap Chrome Extension. Values are stored as human-readable display strings
// so the extension can autofill them directly.
export interface Demographics {
  location_country?: string;           // default 'USA'
  authorized_to_work?: string;         // 'Yes' | 'No' | ''
  require_visa_sponsorship?: string;   // 'Yes' | 'No' | ''
  gender?: string;                     // 'Male' | 'Female' | 'Decline to Answer' | 'Other' | ''
  gender_other?: string;               // free text when gender === 'Other'
  race?: string;                       // one of RACE_OPTIONS | ''
  race_other?: string;                 // free text when race === 'Other'
  veteran?: string;                    // 'Yes' | 'No' | 'Decline to Answer' | 'Other' | ''
  veteran_other?: string;
  disabled?: string;                   // 'Yes' | 'No' | 'Decline to Answer' | 'Other' | ''
  disabled_other?: string;
  // Annual salary expectation floor — digits only, no separators/symbols
  // (e.g. '225000'): the lowest annual salary the user would accept. Never
  // appears on a resume; the Chrome Extension uses it to compute a per-job
  // "calculated salary ask" = max(posted-range average, floor).
  salary_floor?: string;
  salary_currency?: string;            // ISO 4217 code, display formatting only (default 'USD')
}

// Recommendation from AI Resume Review
export interface Recommendation {
  id: string;
  path: string;
  field: string;
  desc: string;
  // 'advise' is informational only: data is null, it is never applied to the
  // resume (no Apply button, skipped by Apply All) — dismiss-only.
  action: 'replace' | 'append' | 'remove' | 'advise';
  data: unknown;
  // Which review pass produced this ('lint' | 'copyedit' | 'redundancy' |
  // 'credibility' | 'holistic'). Absent on legacy recommendations.
  category?: string;
  source?: string;
  // Legacy field for backwards compatibility with old recommendations
  replacement?: Record<string, unknown>;
  status: 'pending' | 'accepted' | 'dismissed';
  created_at: string;
}

// Per-resume section configuration, edited in the Edit Resume lightbox and
// stored on the resume at resumejson.section_settings. The backend resolves a
// section per-job as: per-job override > resume section_settings > legacy user
// preference (default_gen_* / section_names / section_order) > global default.
export interface SectionSettings {
  gen_types?: Partial<Record<SubcomponentType, GenerationType>>;
  names?: Partial<Record<SubcomponentType, string>>;   // custom display titles
  order?: SubcomponentType[];
}

// Per-resume Skills display controls, edited in the Edit Resume lightbox and
// stored on the resume at resumejson.skills_settings. ABSENT = legacy resume:
// the global settings (skills_max_categories, skill_group_max_lines, …) apply
// unchanged. PRESENT = these values override those globals for this resume's
// renders. See features/skills_display_controls.md (binding storage contract).
export interface SkillsSettings {
  // 'sections' = grouped rows with group labels (today's behavior).
  // 'individual' = one flat, comma-delimited best-fit list, no group labels.
  display?: 'sections' | 'individual';
  max_sections?: number;   // cap on named skill groups selected (default 4)
  max_lines?: number;      // lines per group; individual total = sections × lines (default 1)
  // Trailing "Other" line in 'sections' mode: high-relevance skills whose own
  // group was not selected. ABSENT = true (today's behavior); false suppresses
  // the line. Ignored by the 'individual' display, which has no group lines.
  include_other?: boolean;
}

// Resume JSON
export interface ResumeJSON {
  name: string;
  contact: ContactItem[];
  summary: string;
  skills: Skill[];
  skill_groups?: SkillGroup[];
  highlights: string[];
  experience: ExperienceCompany[];
  education: Education[];
  awards: Award[];
  presentations: Presentation[];
  publications: Publication[];
  portfolios: Portfolio[];
  summary_phrases?: SummaryPhrase[];
  // Summary v2 building blocks. Absent = legacy resume (summary_phrases path).
  summary_sections?: SummarySections;
  synonyms?: SynonymSet[];
  // AI-extracted, user-confirmed ground truth (see ImpliedFact).
  implied_facts?: ImpliedFact[];
  // Optional demographics (EEO / voluntary disclosure). Never rendered on a
  // generated resume — used only to pre-fill applications via the Chrome Extension.
  demographics?: Demographics;
  recommendations?: Recommendation[];
  // Scorecard findings the user has manually dismissed ("Mark resolved"). Stored
  // by deterministic finding id; persists with the resume so dismissals stick.
  scorecard_resolved?: string[];
  // Per-resume generation types, custom section names and section order.
  section_settings?: SectionSettings;
  // Per-resume Skills display shape + caps (absent = global settings apply).
  skills_settings?: SkillsSettings;
}

/**
 * Deep-link target for opening Edit Resume at a specific finding.
 *
 * `nonce` is what makes the same finding re-trigger: the editor handles a
 * target once per nonce, so clicking the same action twice (or reopening the
 * editor) scrolls again instead of being swallowed as an unchanged prop.
 */
export interface EditorFocusTarget {
  /** rec-path grammar, e.g. `experience[0].roles[1].bullets[2]`. */
  path?: string | null;
  field?: string | null;
  snippet?: string | null;
  nonce: number;
}

// Resume
export interface Resume {
  userid: string;
  resumename: string;
  resumejson: ResumeJSON;
  lastupdate: string;
  master_resume_path?: string;
  publish_master_resume?: boolean;
  resume_tags?: string; // tag-generation status, 'generated' when complete
  // Implied-facts extraction marker — polled by the editor's parent after close.
  implied_facts_state?: ImpliedFactsState;
  // Role-targeted master resumes (top-level attribute, NOT inside resumejson).
  dedicated_masters?: DedicatedMaster[];
  /**
   * Recruiter Check (BR-1): count of UNRESOLVED high-severity lint findings on
   * this master resume, persisted by the resume save path so the selector can
   * warn ("these get copied onto every resume you generate") without running a
   * lint pass per row. Absent on records saved before the field existed —
   * treated as 0 / no banner.
   */
  lint_high_count?: number;
}

// Dedicated (role-targeted) master resumes.
// One extra master resume per role type, generated against the aggregated tags
// of every job whose TITLE matches the user's keywords — not against a single
// posting. Lives at https://<username>.skillsnap.me/<path>.
export type DedicatedMasterState =
  | 'searching'          // job search + tag aggregation running
  | 'pending_generate'   // saved before the search finished; generation queued
  | 'generating'         // the normal bespoke pipeline is running
  | 'ready'              // published
  | 'error';

export interface DedicatedMaster {
  id: string;
  path: string;
  keywords: string;      // raw text the user typed, replayed on Regenerate
  jobid: string;         // the hidden synthetic job the aggregation produced
  state: DedicatedMasterState;
  matched_jobs?: number;
  tag_count?: number;
  error?: string;
  created_at?: string;
  updated_at?: string;
}

// Aggregation state of the synthetic job, polled by the add-flow modal only.
export type DedicatedMasterAggState = 'searching' | 'done' | 'no_matches' | 'error';

export interface DedicatedMasterSearchResponse {
  jobid: string;
  default_path: string;
  expanded_keywords: string[];
}

export interface DedicatedMasterStatusResponse {
  agg_state: DedicatedMasterAggState;
  matched_jobs?: number;
  tag_count?: number;
  message?: string;
}

// ---- frozen content of a role-targeted resume (preview only) ---------------
// The Edit Resume preview panel draws a SCHEMATIC of a role-targeted resume:
// the CONTENT is frozen from that resume's last generation (lengths matter,
// wording does not) while the STRUCTURE — section order, renames, omit
// toggles, skills display mode, name, contact — comes live from the editor.
// The server strips the per-item score prefixes before returning these, so
// every string here is display-ready. Nothing here carries `include` flags:
// the generated resume already applied them.

export interface DedicatedMasterContentBullet {
  text: string;
}

export interface DedicatedMasterContentRole {
  title: string;
  startDate?: string;
  endDate?: string;
  current?: boolean;
  bullets: DedicatedMasterContentBullet[];
}

export interface DedicatedMasterContentCompany {
  company: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  current?: boolean;
  roles: DedicatedMasterContentRole[];
}

export interface DedicatedMasterContentSkill {
  title: string;
  group_id?: string | null;
}

/** A flat listing item (education / awards / presentations / …). */
export interface DedicatedMasterContentItem {
  title?: string;
  org?: string;
  issuer?: string;
  event?: string;
  year?: string;
  url?: string;
}

export interface DedicatedMasterContent {
  summary?: string;
  highlights?: string[];
  skills?: DedicatedMasterContentSkill[];
  experience?: DedicatedMasterContentCompany[];
  education?: DedicatedMasterContentItem[];
  awards?: DedicatedMasterContentItem[];
  presentations?: DedicatedMasterContentItem[];
  publications?: DedicatedMasterContentItem[];
  portfolios?: DedicatedMasterContentItem[];
  /** ISO timestamp of the generation this content was frozen from. */
  generated_at?: string | null;
}

/** `…/dedicated-masters/status?jobid=…&content=1` — status PLUS the content. */
export interface DedicatedMasterContentResponse extends DedicatedMasterStatusResponse {
  /** null when the generated resume could not be read back. */
  content: DedicatedMasterContent | null;
}

// Job
export interface Job {
  jobid: string;
  postedts: string;
  jobcompany: string;
  joblistid?: string;
  jobtitle: string;
  jobtitlesafe: string;
  jobdesc: string;
  joblocation?: string;
  jobsalary?: string;
  jobposteddate: string;
  joburl?: string;
  jobcompanylogo?: string;
  jobtags: string[];
  super_tags?: string[];  // high-level skill categories for job-tailored grouping
  jobreqid?: string;
  createdAt: string;
  // Junk-proof placeholder card: unsupported job board awaiting an auto-built
  // parser (WQ 4bda76f3). Never enters tag/resume generation until back-filled.
  placeholder?: boolean;
  placeholder_note?: string;
  // Terminal "research failed" state (WQ 15d32673): the card couldn't be
  // auto-researched after bounded retries — salary/location unavailable. Shown
  // with an open-posting link instead of the "in progress" spinner.
  research_failed?: boolean;
  research_note?: string;
}

// Response of POST /jobs/url — richer than a bare UserJob: the backend may
// return a placeholder card (unsupported board) or a list-import summary.
export interface CreateJobFromUrlResponse {
  message?: string;
  job?: Job;
  userJob?: UserJob;
  duplicate?: boolean;
  // A dormant duplicate (Declined/Expired/Skipped/Errored) that this re-add
  // brought back to the Queued phase.
  revived?: boolean;
  shared?: boolean;
  placeholder?: boolean;
  list_import?: { queued: number; dropped: number };
}

// Response of POST /jobs/manual. `duplicate` means nothing was created (the job
// is already in the list); `merged` means an existing DATA-LESS placeholder card
// was filled in with the details just typed. Both used to return silently —
// the modal now always tells the user which happened (incident 2026-07-27).
export interface CreateJobManualResponse {
  message?: string;
  job?: Job;
  userJob?: UserJob;
  duplicate?: boolean;
  merged?: boolean;
  matched_company?: string;
  matched_title?: string;
  matched_on?: string;
}

// User Job (combined with Job for display)
export interface UserJob extends Job {
  userid: string;
  resumeid?: string;
  jobphase: JobPhase;

  /** Free-form per-user note about this job. Lives on the user-job record ONLY
   *  (job records are shared across users), and is dropped from the /jobs list
   *  payload — the editor loads it via getJob(). */
  jobnotes?: string;

  // Interview-question matches (populated when a card enters the Interviewing
  // phase — see shared/interview_match.py). Each is a matched question id + its
  // relevance score, sorted by score descending.
  interview_matches?: { qid: string; score: number }[];
  interview_matched_at?: string;
  // Client-only transient: 'matching' while the phase-change request that
  // computes the matches is in flight (server never persists this).
  interview_match_state?: 'matching' | 'done';

  // Candidate name (from resume)
  dataname?: string;
  // Subcomponent data
  datacontact?: string;
  datasummary?: string;
  dataskills?: string;
  datahighlights?: string;
  dataexperience?: string;
  dataeducation?: string;
  dataawards?: string;
  datapresentations?: string;
  datapublications?: string;
  dataportfolios?: string;
  datacoverletter?: string;
  dataelevatorpitch?: string;

  // Generation states
  statecontact: GenerationState;
  statesummary: GenerationState;
  stateskills: GenerationState;
  statehighlights: GenerationState;
  stateexperience: GenerationState;
  stateeducation: GenerationState;
  stateawards: GenerationState;
  statepresentations: GenerationState;
  statepublications: GenerationState;
  stateportfolios: GenerationState;
  statecoverletter: GenerationState;
  stateelevatorpitch: GenerationState;

  // Generation types
  typecontact: GenerationType;
  typesummary: GenerationType;
  typeskills: GenerationType;
  typehighlights: GenerationType;
  typeexperience: GenerationType;
  typeeducation: GenerationType;
  typeawards: GenerationType;
  typepresentations: GenerationType;
  typepublications: GenerationType;
  typeportfolios: GenerationType;
  typecoverletter: GenerationType;
  typeelevatorpitch: GenerationType;

  // Free-generation enablement (per user-job)
  is_enabled?: boolean;
  enabled_at?: string | null;

  // Final file locations
  s3locresumehtml?: string;
  s3locresumepdf?: string;
  s3loccoverletterhtml?: string;
  s3loccoverletterpdf?: string;
  s3locresumetxt?: string;
  s3loccoverlettertxt?: string;
  s3locresumetemplate?: string;

  // Feedback on generated files
  feedback_resume?: 'U' | 'D';
  feedback_coverletter?: 'U' | 'D';

  // Calculated fields
  postingAge?: number;
  updatedAt: string;

  // Phase-change tracking (Activity Report)
  phase_changed_at?: string;
  phase_history?: PhaseHistoryEntry[];

  // Quality of the GENERATED resume for this job — scored server-side just
  // before the Ready flip (generation/final_files.compute_output_quality) from
  // the final rendered content, not from the master resume. Stored as a compact
  // JSON string (see OutputQuality); absent on jobs generated before Wave 6.
  output_quality?: string;

  /**
   * Recruiter Check (BR-7): the user chose "Apply anyway" on a C/D-graded
   * resume, so the apply-time confirm never asks again for this card. Written
   * by the phase-update call that carried `recruiter_ack: '1'`.
   */
  recruiter_ack?: string;
}

// Parsed shape of `UserJob.output_quality`. Written by
// `final_files.build_output_quality_payload` — keep the two in step.
//
// Recruiter Check (BR-5/BR-6) ADDS the grade fields below. They are optional on
// purpose: a payload without `grade` is a legacy (pre-Recruiter-Check) render
// and must keep rendering the original High/Med/Low view.
export interface OutputQuality {
  v: number;
  score: number;              // 0-100
  band: 'high' | 'med' | 'low';
  counts: { high: number; med: number; low: number; fab: number };
  top: string[];              // up to 3 short coaching notes
  citations_missing: string[]; // companies a highlight cites that the page no longer shows
  lint_version?: string;
  at?: string;
  // ── Recruiter Check (absent ⇒ legacy payload) ────────────────────────────
  grade?: RecruiterGrade;
  verdict?: string;
  rc?: RecruiterCheckSummary;
  impression?: string;
}

/** A / B / C / D — the Recruiter Grade shown on the job card's chip. */
export type RecruiterGrade = 'A' | 'B' | 'C' | 'D';

/** Per-check counts + the caps that held the grade down, stored on the record. */
export interface RecruiterCheckSummary {
  counts: Partial<Record<RecruiterCheckName, number>>;
  caps: string[];
  v: number;
}

export type RecruiterCheckName =
  | 'level_match' | 'vocab' | 'skills_soup' | 'age_signal' | 'highlights'
  | 'sentence_case' | 'master_lint';

/** What a finding's button does. Unknown kinds are ignored, never rendered dead. */
export interface RecruiterAction {
  kind: string;
  label: string;
  target?: string | null;
}

/**
 * One Recruiter Check finding, computed on demand by
 * `POST /jobs/{jobid}/quality-recheck`. Every key is always present in the
 * response; the client still treats each defensively.
 */
export interface RecruiterFinding {
  id: string;
  check: RecruiterCheckName;
  /** 'master' = fix once, fixes every resume; 'job' = fix for this job. */
  group: 'master' | 'job';
  severity: 'high' | 'med' | 'low';
  where: string;
  what: string;
  why: string;
  how: string;
  actions: RecruiterAction[];
  can_fix: boolean;
  suggestion: string | null;
  path: string | null;
  field: string | null;
  snippet: string | null;
  caps_grade: 'C' | 'D' | null;
}

/**
 * Response of `POST /jobs/{jobid}/quality-recheck`.
 *
 * `status: 'no_files'` is a 200, NOT an error: the rendered artifacts this
 * check reads (Resume.txt) are gone or were never written, so the lightbox
 * asks the user to regenerate instead of showing a failure.
 */
export interface RecruiterCheckResponse {
  status: 'ok' | 'no_files' | string;
  grade?: RecruiterGrade;
  verdict_line?: string;
  score?: number;
  caps?: string[];
  findings?: RecruiterFinding[];
  impression?: string | null;
  impression_available?: boolean;
  checked_at?: string;
  rc_version?: number;
  message?: string;
}

// One phase-change event on a job card. Appended by the backend on every
// jobphase transition (shared/dynamodb.update_user_job); legacy jobs that
// predate tracking may have no history at all.
export interface PhaseHistoryEntry {
  ts: string; // ISO timestamp
  phase: JobPhase | string;
}

// --- New orthogonal user model ---
// Role: permissions/access level
export type Role = 'user' | 'admin' | 'snappy' | 'test';

// Status: feature gating
export type Status = 'trial' | 'active' | 'hibernating' | 'cancelled' | 'expired';

// Plan: billing model
export type Plan = 'free' | 'consumer' | 'enterprise';


// User Preferences
// Abstract writing-voice fingerprint (closed-vocabulary enum traits only — never
// the raw sample, phrases, or facts). Written server-side by POST /writing-style,
// read-only on the client. Mirrors features/user_writing_style.md §2.
// One entry per corpus contributor measured into the fingerprint (v2). `chars`
// is an integer count only — never the text itself.
export interface WritingFingerprintSource {
  type: 'sample' | 'interview_qa' | 'application_qa' | 'resume_summary' | 'edited_output';
  chars: number;
}

// Integer-only style counters (v2). Nothing here is derivable back to content,
// which is why it is safe to persist. All fields optional for forward/back compat.
export interface WritingFingerprintStats {
  sentences?: number;
  words?: number;
  word_chars?: number;
  sent_len_sumsq?: number;
  em_dashes?: number;
  semicolons?: number;
  parentheticals?: number;
  exclamations?: number;
  contractions?: number;
  connective_counts?: Record<string, number>;
}

export interface WritingFingerprint {
  version: number; // 1 (legacy) or 2 (hybrid measured + judged)
  created_at: string;
  prompt_id: string;
  sample_chars: number;
  // v2 additions (absent on v1 fingerprints):
  sources?: WritingFingerprintSource[];
  stats?: WritingFingerprintStats;
  // traits may include the v2 `word_length` key ('short' | 'medium' | 'long').
  traits: Record<string, string | string[]>;
  // Traits the user has explicitly fine-tuned via calibration (§11.4).
  calibrated?: string[];
}

// Result of one calibration round — two rewrites of a canned paragraph the
// client shows side by side (order randomized). `current_value` is null when
// the trait is absent (the omit-when-unsure case). See §11.4.
export interface CalibrateRoundResult {
  trait: string;
  current_value: string | null;
  flipped_value: string;
  paragraph_id: string;
  text_current: string;
  text_flipped: string;
}

export interface UserPreferences {
  default_gen_contact: GenerationType;
  default_gen_summary: GenerationType;
  default_gen_skills: GenerationType;
  default_gen_highlights: GenerationType;
  default_gen_experience: GenerationType;
  default_gen_education: GenerationType;
  default_gen_awards: GenerationType;
  default_gen_presentations: GenerationType;
  default_gen_publications: GenerationType;
  default_gen_portfolios: GenerationType;
  default_gen_coverletter: GenerationType;
  // Resume Generation Options
  show_skillsnap_url?: boolean;
  show_year_education?: boolean;
  show_year_awards?: boolean;
  show_year_presentations?: boolean;
  show_year_publications?: boolean;
  show_year_portfolios?: boolean;
  cutoff_year?: number;
  target_pages?: number | null;
  bullet_period_style?: string;
  help_panel_open?: boolean;
  marketing_email_optin?: boolean;
  section_names?: string;
  section_order?: string;
  /**
   * Resumename last picked in the header resume selector, restored on load so
   * the selection is sticky across sessions and devices. Server-side rather
   * than localStorage because logout clears local storage.
   */
  selected_resume?: string;
  admin_timezone?: string;
  /**
   * Personal spell-check wordlist (lowercase). Grown by "Add to My Dictionary"
   * on a Scorecard spelling finding; the lint endpoint reads it so the word is
   * never flagged again, on any resume. Capped client-side (see
   * CUSTOM_DICTIONARY_MAX in utils/scorecard).
   */
  custom_dictionary?: string[];
  // App Options - Active Phases (Days before moving to Expired)
  days_queued_before_expired?: number;
  days_generating_before_expired?: number;
  days_ready_before_expired?: number;
  days_applied_before_expired?: number;
  days_followup_before_expired?: number;
  days_negotiation_before_expired?: number;
  days_accepted_before_expired?: number;
  days_declined_before_expired?: number;
  // App Options - Terminal Phases (Days before Trash/Delete)
  days_skipped_before_trashed?: number;
  days_errored_before_trashed?: number;
  days_expired_before_trashed?: number;
  days_trash_before_deleted?: number;
  // New orthogonal user model
  role?: Role;
  status?: Status;
  plan?: Plan;
  beta_tester?: boolean; // beta-tester program flag (missing == false)
  writing_fingerprint?: WritingFingerprint | null; // read-only; written by POST /writing-style
  email?: string; // login email — read-only, for display/verification only
  stripe_customer_id?: string;
  org_id?: string;
  org_name?: string;
  // Usage tracking
  jobs_created_this_month?: number;
  monthly_job_limit?: number;
  // Watermark & name link settings
  add_skillsnap_watermark?: boolean;
  link_name_to_url?: boolean;
  auto_download_files?: boolean;
  master_resume_format?: 'html' | 'pdf';
  // Per-user overrides (from admin)
  allowed_master_resumes?: number | null;
  allowed_job_cards_per_month?: number | null;
  // Free generation budget (trial + hibernating)
  free_gen_remaining?: number;
  free_gen_last_refill?: string;
  // Authorization-derived flags
  requires_watermark?: boolean;
  can_regenerate_section?: boolean;
}

// API Response Types
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface JobListResponse {
  jobs: UserJob[];
  count: number;
  // Present on the first page of a paged (limit=N) aggregate fetch and on all
  // legacy full fetches; absent on cursor-continuation pages.
  phaseCounts: Record<JobPhase, number>;
  totalCount: number;
  // Paged fetches only: opaque next-page token (null/absent when exhausted).
  cursor?: string | null;
}

export interface ResumeListResponse {
  resumes: Resume[];
  count: number;
}

export interface GenerationStatusResponse {
  jobid: string;
  phase: JobPhase;
  overallStatus: 'ready' | 'generating' | 'complete' | 'error';
  subcomponents: Record<SubcomponentType, {
    state: GenerationState;
    type: GenerationType;
    hasContent: boolean;
  }>;
  finalFiles: {
    resumeHtml: { ready: boolean; generated: boolean; s3loc: string };
    resumePdf: { ready: boolean; generated: boolean; s3loc: string };
    coverLetterHtml: { ready: boolean; generated: boolean; s3loc: string };
    coverLetterPdf: { ready: boolean; generated: boolean; s3loc: string };
  };
  allComplete: boolean;
}

// User
export interface User {
  userid: string;
  userhandle: string;
  email: string;
  username: string;
}

// Application Answers (user-authored Q&A for application forms)
export interface ApplicationAnswer {
  userid: string;
  qaid: string;
  question: string;
  answer: string;
  recommendations: string;
  linked_jobids: string[];
  created_at: string;
  updated_at: string;
  cloned_from?: string;
}

export interface ApplicationAnswersListResponse {
  answers: ApplicationAnswer[];
  count: number;
  trial: boolean;
  trial_max: number;
}

// Interview Q&A (shared question library + per-user practice data)
export interface InterviewQuestion {
  userid: string; // 'public' for the shared library, else the owner's uuid
  qid: string;
  question: string;
  tags: string[];
  source?: string; // 'seed' | 'user'
  created_at?: string;
  updated_at?: string;
}

export interface InterviewUserData {
  starred: boolean;
  answer: string;
  recommendations: string;
  updated_at?: string;
}

export interface InterviewListResponse {
  questions: InterviewQuestion[];
  user_data: Record<string, InterviewUserData>;
  count: number;
  active: boolean; // status=='active' — gates the Get Feedback button
}

// Import Types
export interface ImportUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface ImportProcessResponse {
  resumejson: ResumeJSON;
  warnings?: string[];
  source: 'yaml' | 'json' | 'pdf';
}

// Organization Types
export interface Organization {
  org_id: string;
  org_name: string;
  billing_contact?: string;
  billing_email?: string;
  renewal_date?: string;
  member_count?: number;
  status?: 'active' | 'inactive' | 'suspended';
  created_at?: string;
  updated_at?: string;
}

export interface JoinOrganizationResponse {
  message: string;
  organization: {
    org_id: string;
    org_name: string;
  };
}
